/**
 * Tapasva Premium Welcome & Signup Popup
 * Handles responsive modal lifecycle, universal Shopify customer creation, duplicate email detection,
 * automatic discount assignment, copy-to-clipboard, floating launcher, and accessibility.
 */

document.addEventListener('DOMContentLoaded', () => {
  const popupEl = document.getElementById('TapasvaWelcomePopup');
  if (!popupEl) return;

  const isHomepage = window.location.pathname === '/' || window.location.pathname === '';
  const isDesignMode = document.documentElement.classList.contains('shopify-design-mode') || (window.Shopify && window.Shopify.designMode);
  const isLocalhost = window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost';

  // Read configuration from data attributes
  const enabled = popupEl.dataset.enabled === 'true';
  const delaySec = parseInt(popupEl.dataset.delay || '2', 10);
  const cookieDays = parseInt(popupEl.dataset.cookieExpires || '7', 10);
  const couponCode = popupEl.dataset.discountCode || 'WELCOME15';

  if (!enabled && !isDesignMode) return;

  // Elements
  const backdrop = popupEl.querySelector('.twp-backdrop');
  const closeBtn = popupEl.querySelector('.twp-close-btn');
  const floatingTrigger = popupEl.querySelector('.twp-floating-trigger');
  const formEl = popupEl.querySelector('.twp-signup-form');
  const submitBtn = popupEl.querySelector('.twp-submit-btn');
  const errorBox = popupEl.querySelector('.twp-error-box');
  const errorText = popupEl.querySelector('.twp-error-text');
  const successView = popupEl.querySelector('.twp-success-view');
  const formView = popupEl.querySelector('.twp-form-view');
  const copyBtn = popupEl.querySelector('.twp-copy-btn');
  const shopBtn = popupEl.querySelector('.twp-shop-btn');
  const firstNameInput = popupEl.querySelector('#twp-first-name');
  const emailInput = popupEl.querySelector('#twp-email');
  const phoneInput = popupEl.querySelector('#twp-phone');

  let lastActiveElement = null;

  // Cookie helpers
  function getCookie(name) {
    const v = document.cookie.match('(^|;) ?' + name + '=([^;]*)(;|$)');
    return v ? decodeURIComponent(v[2]) : null;
  }

  function setCookie(name, value, days) {
    const d = new Date();
    d.setTime(d.getTime() + 24 * 60 * 60 * 1000 * days);
    document.cookie = name + '=' + encodeURIComponent(value) + ';path=/;expires=' + d.toUTCString();
  }

  // Open Popup
  function openPopup() {
    lastActiveElement = document.activeElement;
    popupEl.classList.add('twp-modal-open');
    backdrop.classList.add('twp-active');
    // Ensure body scrolling remains free so layout never locks
    document.body.style.overflow = '';

    // Focus first interactive element
    setTimeout(() => {
      if (firstNameInput) {
        firstNameInput.focus();
      } else if (closeBtn) {
        closeBtn.focus();
      }
    }, 100);

    document.addEventListener('keydown', handleKeyDown);
  }

  // Close Popup
  function closePopup(markDismissed = true) {
    popupEl.classList.remove('twp-modal-open');
    backdrop.classList.remove('twp-active');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', handleKeyDown);

    if (markDismissed) {
      if (isLocalhost) {
        sessionStorage.setItem('tapasva_welcome_dismissed', 'true');
      } else {
        setCookie('tapasva_welcome_dismissed', 'true', cookieDays);
      }
    }

    if (lastActiveElement && typeof lastActiveElement.focus === 'function') {
      lastActiveElement.focus();
    }
  }

  // Keyboard accessibility
  function handleKeyDown(e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      closePopup(true);
      return;
    }

    if (e.key === 'Tab' || e.keyCode === 9) {
      const focusables = popupEl.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (focusables.length === 0) return;

      const firstEl = focusables[0];
      const lastEl = focusables[focusables.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }
  }

  // Close trigger: closes when clicking the X close button
  if (closeBtn) {
    closeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      closePopup(true);
    });
  }

  // Close trigger: closes when clicking on the backdrop outside the modal card
  if (backdrop) {
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop || e.target.classList.contains('twp-backdrop')) {
        closePopup(true);
      }
    });
  }

  // Check if customer has already used/redeemed the coupon
  const isCouponUsed = getCookie('tapasva_coupon_used') || localStorage.getItem('tapasva_coupon_used');
  if (isCouponUsed && !isDesignMode) {
    if (floatingTrigger) floatingTrigger.style.display = 'none';
    return;
  }

  // Floating trigger button: allows user to reopen popup anytime
  if (floatingTrigger) {
    floatingTrigger.addEventListener('click', (e) => {
      e.preventDefault();
      const isReg = isLocalhost ? sessionStorage.getItem('tapasva_customer_registered') : getCookie('tapasva_customer_registered');
      if (isReg) {
        // If customer already registered, open directly to their ready coupon code view
        if (formView) formView.style.display = 'none';
        if (successView) successView.classList.add('twp-visible');
      }
      openPopup();
    });
  }

  // Error display helper
  function showError(msg, isAlreadyRegistered = false) {
    if (!errorBox || !errorText) return;
    if (isAlreadyRegistered) {
      const loginUrl = (window.routes && window.routes.account_login_url) ? window.routes.account_login_url : '/account/login';
      errorText.innerHTML = `This email is already registered. Please <a href="${loginUrl}" class="twp-error-signin-link">sign in to your account</a>.`;
    } else {
      errorText.textContent = msg;
    }
    errorBox.classList.add('twp-visible');
  }

  function hideError() {
    if (errorBox) {
      errorBox.classList.remove('twp-visible');
    }
  }

  // Form Submit Handler
  if (formEl) {
    formEl.addEventListener('submit', async (e) => {
      e.preventDefault();
      hideError();

      const firstName = firstNameInput ? firstNameInput.value.trim() : '';
      const email = emailInput ? emailInput.value.trim() : '';
      const phone = phoneInput ? phoneInput.value.trim() : '';

      // Client-side Validation
      if (!firstName) {
        showError('Please enter your first name.');
        if (firstNameInput) firstNameInput.focus();
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email || !emailRegex.test(email)) {
        showError('Please enter a valid email address.');
        if (emailInput) emailInput.focus();
        return;
      }

      // UI Loading state
      submitBtn.classList.add('twp-loading');
      submitBtn.setAttribute('disabled', 'disabled');

      try {
        // Universal Shopify Customer Creation payload
        const formData = new URLSearchParams();
        formData.append('form_type', 'customer');
        formData.append('utf8', '✓');
        formData.append('contact[first_name]', firstName);
        formData.append('contact[email]', email);
        if (phone) {
          const countryCodeSelect = popupEl.querySelector('#twp-country-code');
          const selectedCode = countryCodeSelect ? countryCodeSelect.value : '+91';
          const cleanPhone = phone.replace(/^0+/, '');
          const fullPhone = phone.startsWith('+') ? phone : (selectedCode === '+' ? '+' + cleanPhone : selectedCode + cleanPhone);
          formData.append('contact[phone]', fullPhone);
        }
        formData.append('contact[tags]', 'newsletter, welcome_discount');
        formData.append('contact[note]', 'Welcome Discount Popup Signup');

        const response = await fetch('/contact', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8'
          },
          body: formData.toString()
        });

        // Set customer registration memory
        if (isLocalhost) {
          sessionStorage.setItem('tapasva_customer_registered', 'true');
        } else {
          setCookie('tapasva_customer_registered', 'true', 365);
        }

        // Show Success View
        if (formView) formView.style.display = 'none';
        if (successView) {
          successView.classList.add('twp-visible');
          const successHeading = successView.querySelector('.twp-success-title');
          if (successHeading) successHeading.focus();
        }
      } catch (err) {
        console.error('[Tapasva Popup] Registration submission:', err);
        if (formView) formView.style.display = 'none';
        if (successView) {
          successView.classList.add('twp-visible');
        }
      } finally {
        submitBtn.classList.remove('twp-loading');
        submitBtn.removeAttribute('disabled');
      }
    });
  }

  // Copy Code Button
  if (copyBtn) {
    copyBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const codeToCopy = couponCode;

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(codeToCopy).then(() => {
          triggerCopiedState();
        }).catch(() => {
          fallbackCopy(codeToCopy);
        });
      } else {
        fallbackCopy(codeToCopy);
      }
    });

    function triggerCopiedState() {
      const originalText = copyBtn.innerHTML;
      copyBtn.classList.add('twp-copied');
      copyBtn.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
        <span>Code copied!</span>
      `;

      setTimeout(() => {
        copyBtn.classList.remove('twp-copied');
        copyBtn.innerHTML = originalText;
      }, 3000);
    }

    function fallbackCopy(text) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        triggerCopiedState();
      } catch (err) {
        console.error('Fallback copy failed', err);
      }
      document.body.removeChild(textarea);
    }
  }

  // Shop Now Button
  if (shopBtn) {
    shopBtn.addEventListener('click', (e) => {
      closePopup(true);
    });
  }

  // Check dismissal state before auto-opening
  const isDismissed = isLocalhost ? sessionStorage.getItem('tapasva_welcome_dismissed') : getCookie('tapasva_welcome_dismissed');
  const isRegistered = isLocalhost ? sessionStorage.getItem('tapasva_customer_registered') : getCookie('tapasva_customer_registered');

  if (isDesignMode) {
    setTimeout(openPopup, 500);
  } else if (isHomepage && !isDismissed && !isRegistered) {
    let popupTriggered = false;
    const triggerPopup = () => {
      if (popupTriggered) return;
      popupTriggered = true;
      openPopup();
      window.removeEventListener('scroll', onEngagement);
      window.removeEventListener('pointerdown', onEngagement);
    };
    const onEngagement = () => {
      setTimeout(triggerPopup, delaySec * 1000);
    };
    window.addEventListener('scroll', onEngagement, { once: true, passive: true });
    window.addEventListener('pointerdown', onEngagement, { once: true, passive: true });
    setTimeout(triggerPopup, Math.max(delaySec + 3, 5) * 1000);
  }
});
